// When the till may reload to a newer bundle (INT-7, plan section 10).
//
// A reload is safe only when it loses nothing. The draft survives one, but an
// open sheet, a running Undo, details typed before the first item, a sale
// waiting for its answer, a receipt still on its way to the printer, a
// decision on a held print still on its way and an address removal not yet
// sent all live in the page alone. The till moves only
// when none of them is there.
//
// Inside the Bosla app (#392) a newer screen comes from the bundles the order
// store kept, not from the origin: while the till is at rest it asks the
// shell to switch (switchAtRest in shell.ts), at once and then every
// SWITCH_ASK_MS, and the shell switches only when it holds a newer bundle. The
// lock screen is at rest too when nothing waits behind it, no cart and no sale
// the server has not answered, and nobody is at it.
//
// A lock also installs a newer Bosla app (UC-57, #650): the lock screen, and
// the till's own lock (the Lock button, the cashier's name, the idle lock;
// Roman's answers on #650 and #925), each with nobody at it. At each of its
// asks it asks the shell to install an app update the store kept first, and
// to switch screen bundles only when none installs (askAtLock). An install
// stops the store and the print host, so no write may be on its way.
//
// The till's own lock asks to install over any view, with a cart, customer
// details, a note or an open order (#925): the draft survives the update and
// the store holds open orders. It waits only while a write is on its way
// (writeInFlight) or an open form holds typing that only the page keeps
// (lockedTillAtRest), and asks only while the store's print queue holds no
// job on its way (installWhenQuiet in appUpdate.svelte.ts). Away from an
// idle Sell it asks to install alone: a switch keeps the strict rule
// (tillAtRest). The signed-out lock screen, which the store does not show
// its queue, asks with no cart and no unanswered sale behind it, and only
// once it has been at rest longer than any print waits for its printer
// (settledLockAsk). An unlocked Sell asks to switch only, and a till's own
// lock with someone at it asks nothing (tillAsk).

/** What the till holds that a reload would lose. */
export interface TillState {
	view: string;
	sheetOpen: boolean;
	mineOpen: boolean;
	displayOpen: boolean;
	undoRunning: boolean;
	paying: boolean;
	retrying: boolean;
	pendingSale: boolean;
	/** A receipt job is watched, or its print cooldown runs. */
	printing: boolean;
	/**
	 * A staff decision on a held print is on its way (#515), as a print is.
	 * A held print alone is not here: it waits for a person, whatever its
	 * age, and must never keep the till from a newer screen.
	 */
	deciding: boolean;
	/** A manager's address removal waits out its Undo, or is on its way. */
	addressRemoval: boolean;
	/**
	 * A sent order is open on Sell (#348): the cashier is with its customer,
	 * about to add to it or take its payment. A reload loses nothing of it,
	 * since the store holds it and the draft names it, but would move the
	 * screen under the cashier mid-order.
	 */
	openOrder: boolean;
	draft: {
		lines: number;
		customerPhone: string;
		customerName: string;
		deliveryAddress: string;
		deliveryLandmark: string;
		note: string;
	};
}

/** Whether a reload now loses nothing. */
export function tillAtRest(s: TillState): boolean {
	return (
		s.view === 'sell' &&
		!s.sheetOpen &&
		!s.mineOpen &&
		!s.displayOpen &&
		!s.undoRunning &&
		!s.paying &&
		!s.retrying &&
		!s.pendingSale &&
		!s.printing &&
		!s.deciding &&
		!s.addressRemoval &&
		!s.openOrder &&
		s.draft.lines === 0 &&
		s.draft.customerPhone === '' &&
		s.draft.customerName === '' &&
		s.draft.deliveryAddress === '' &&
		s.draft.deliveryLandmark === '' &&
		s.draft.note === ''
	);
}

/** What the lock screen holds that a reload would lose. */
export interface LockState {
	/** A cart, or a sale the server has not answered, waits behind it. */
	waiting: boolean;
	/** A cashier is at it: a name picked, digits typed, a check, a refusal or a lockout shown. */
	typing: boolean;
}

/** Whether a reload of the lock screen now loses nothing. */
export function lockAtRest(s: LockState): boolean {
	return !s.waiting && !s.typing;
}

/** How often a till at rest inside the Bosla app asks the shell to switch. */
export const SWITCH_ASK_MS = 30_000;

/** The till's own lock (T4), which picks the signed-in cashier itself. */
export interface OwnLockState {
	/** The digits typed. */
	digits: string;
	/** The pad's phase: idle, checking, a refusal or a lockout. */
	phase: string;
	/** Who the pad is for; null once "Not you?" asks for a name. */
	pickedId: string | null;
	/** The cashier signed in, whom the lock picks itself. */
	signedInId: string;
}

/**
 * Whether nobody is at the till's own lock: its own cashier still picked, no
 * digit typed, and no check, refusal or lockout shown.
 */
export function ownLockAtRest(s: OwnLockState): boolean {
	return s.digits === '' && s.phase === 'idle' && s.pickedId === s.signedInId;
}

/**
 * The page's writes on their way (#925). An install stops the order store, so
 * each would be left with no answer. A held print alone is none of them: it
 * waits for a person.
 */
export interface Writes {
	paying: boolean;
	retrying: boolean;
	pendingSale: boolean;
	/**
	 * Any other change on its way to the store: an order sent, a discount, an
	 * Orders action (a payment, a void, a reprint, a no-sale), a cancellation,
	 * a manager's approval, My items, a till setup step or a cash point.
	 */
	saving: boolean;
	/** A receipt job is watched, or its print cooldown runs. */
	printing: boolean;
	/** A drawer kick is watched. */
	drawer: boolean;
	/** A staff decision on a held print is on its way (#515). */
	deciding: boolean;
	/** A manager's address removal waits out its Undo, or is on its way. */
	addressRemoval: boolean;
}

/** Whether any write of the page is on its way. */
export function writeInFlight(w: Writes): boolean {
	return (
		w.paying ||
		w.retrying ||
		w.pendingSale ||
		w.saving ||
		w.printing ||
		w.drawer ||
		w.deciding ||
		w.addressRemoval
	);
}

/** What the till holds under its own lock that an install would lose (#925). */
export interface LockedTillState extends Writes {
	/**
	 * A sheet over Sell holds what the cashier picked or typed: the line
	 * editor, the delivery details or Payment. Done holds nothing.
	 */
	sellSheet: boolean;
	/**
	 * Another open form holds typing that only the page keeps: My items, a
	 * printer's address or its identify sheet, a cash point picked and not
	 * confirmed, an opening float or a Close shift count.
	 */
	formOpen: boolean;
	/** A removed line's Undo runs. */
	undoRunning: boolean;
}

/**
 * Whether the till's own lock may install now: no write on its way and no
 * form holding typing. The view, the cart, customer details, a note and an
 * open order do not count: the draft survives the update and the store holds
 * open orders (#925).
 */
export function lockedTillAtRest(s: LockedTillState): boolean {
	return !writeInFlight(s) && !s.sellSheet && !s.formOpen && !s.undoRunning;
}

/**
 * What the till asks the shell: nothing, a switch, an install and then a
 * switch, or an install alone.
 */
export type TillAsk = 'nothing' | 'switch' | 'install' | 'installOnly';

/**
 * What the till asks while it is signed in. Unlocked, a switch at rest
 * (`atRest`, tillAtRest's strict rule) and nothing otherwise. Under its own
 * lock, nothing while somebody is at it (`lockAtRestNow`), because a reload
 * would take what they typed, or while the lock's own rule says no
 * (`lockedAtRest`, lockedTillAtRest); otherwise an install first, then a
 * switch only at strict rest (#925).
 */
export function tillAsk(
	atRest: boolean,
	locked: boolean,
	lockAtRestNow: boolean,
	lockedAtRest: boolean,
): TillAsk {
	if (!locked) return atRest ? 'switch' : 'nothing';
	if (!lockAtRestNow || !lockedAtRest) return 'nothing';
	return atRest ? 'install' : 'installOnly';
}

/**
 * Whether a pressed Install now asks the shell now (#925): only on an
 * unlocked till, only while the lock's own rule would let it (`lockedAtRest`:
 * no write on its way and no form holding typing), and only while no sheet a
 * lock closes is open (`sheetOpen`: Discount, Cancel item, Manager approval
 * and its pad, Pick a table), since those hold typing or a pick too. A lock
 * ends the wait, and from then on the lock's own ask installs, with nobody at
 * its pad (tillAsk).
 */
export function installNowAsks(
	waiting: boolean,
	locked: boolean,
	lockedAtRest: boolean,
	sheetOpen: boolean,
): boolean {
	return waiting && !locked && lockedAtRest && !sheetOpen;
}

/**
 * The lock screen's ask at rest: install a newer app update the store kept,
 * and only when no install starts, switch to a newer screen bundle. An install
 * ask that fails still asks to switch. `atRest` says whether the screen is
 * still at rest: an install ask can take seconds while the shell checks the
 * file, and a cashier may come to the lock meanwhile, so nothing more is asked
 * once it is false. The install ask is given `atRest` too, to ask again
 * between its own calls (installWhenQuiet). True when either started.
 */
export async function askAtLock(
	install: (atRest: () => boolean) => Promise<boolean>,
	switchBundle: () => Promise<boolean>,
	atRest: () => boolean = () => true,
): Promise<boolean> {
	if (!atRest()) return false;
	let installing = false;
	try {
		installing = await install(atRest);
	} catch {
		// The shell started no install, so the screen may still switch.
	}
	if (installing) return true;
	return atRest() && (await switchBundle());
}

/**
 * How long the signed-out lock screen stays at rest before it asks to
 * install: a minute past the longest a print of the till may stay on its
 * way. A kitchen slip waits for its printer 5 minutes at most, and every
 * other job less (`DeadlineFor` in internal/core/domain/printjob.go); the
 * store ends every job by its deadline, printed or failed. With nobody signed in, the till queues no new
 * job, so once this time has passed at rest no print of it is on its way.
 */
export const PRINTS_SETTLED_MS = 6 * 60_000;

/**
 * The signed-out lock screen's ask, for one stretch at rest: until the
 * stretch has lasted `PRINTS_SETTLED_MS` it only asks to switch, and from
 * then on it asks to install first (askAtLock). The store lists its print
 * queue only to a signed-in session, so this screen cannot see a print on its
 * way, and the wait stands for that check (decision
 * 2026-10-27-app-update-installs-at-any-safe-moment: no write on its way). The
 * till's own lock, which has a session, checks the queue instead
 * (installWhenQuiet). The screen reads no clock (NN-TIME): it counts its
 * turns, one every `every` ms as askWhileAtRest asks, and a turn that comes
 * late only makes the wait longer.
 */
export function settledLockAsk(
	install: () => Promise<boolean>,
	switchBundle: () => Promise<boolean>,
	every: number = SWITCH_ASK_MS,
): (atRest: () => boolean) => Promise<boolean> {
	let turns = 0;
	return async (atRest) => {
		const waited = turns * every;
		turns += 1;
		if (waited >= PRINTS_SETTLED_MS) return askAtLock(install, switchBundle, atRest);
		return atRest() && (await switchBundle());
	};
}

/**
 * While the till stays at rest, ask the shell to switch to a newer screen
 * bundle: at once, then every `every` ms. Returns the stop, which the till
 * calls as soon as it leaves rest. A failed ask is asked again at the next
 * turn. The ask is given whether the till is still at rest, false once the
 * stop ran, for an ask that waits between two calls to the shell.
 */
export function askWhileAtRest(
	ask: (atRest: () => boolean) => Promise<unknown>,
	every: number = SWITCH_ASK_MS,
): () => void {
	let stopped = false;
	const atRest = () => !stopped;
	const once = () => {
		if (!stopped) void ask(atRest).catch(() => {});
	};
	once();
	const timer = setInterval(once, every);
	return () => {
		stopped = true;
		clearInterval(timer);
	};
}
